<template>
  <div class="cht-root">
    <div id="layout-wrapper" class="cht-layout-wrapper">
      <!-- TOP BAR -->
      <div class="cht-topbar">
        <div class="container d-flex align-items-center gap-3">
          <button class="cht-back-btn" @click="goBack"><i class="mdi mdi-arrow-left"></i></button>
          <span v-if="cameFromNotifications" class="cht-from-notif-pill" @click="$router.push('/notifications')"><i class="mdi mdi-bell-outline me-1"></i>Notifications</span>
          <div class="cht-avatar-wrap" :class="{ 'cht-online': otherOnline }">
            <user-avatar :src="otherAvatar" :name="otherName" size="md" />
          </div>
          <div class="flex-fill" style="min-width:0;">
            <div style="color:var(--fm-h,#F5F0E8);font-size:14px;font-weight:600;">{{ otherName || 'Chat' }}</div>
            <div v-if="otherOnline" style="color:#50C878;font-size:11px;">online</div>
            <div v-else-if="otherLastSeen" style="color:var(--fm-s,#6B6356);font-size:11px;">last seen {{ fmtLastSeen(otherLastSeen) }}</div>
            <div v-else-if="contextLabel" style="color:var(--fm-s,#6B6356);font-size:11px;">{{ contextLabel }}</div>
          </div>
        </div>
      </div>

      <!-- MESSAGES BODY -->
      <div class="cht-body" ref="chatBody">
        <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
        <template v-else>
          <button v-if="hasMore" class="btn btn-outline-secondary btn-sm d-block mx-auto mb-3" @click="loadMore" :disabled="loadingMore">Load older</button>

          <template v-for="(msg, i) in sortedMessages" :key="msg.id || i">
            <!-- Date separator -->
            <div v-if="showDateSep(i)" class="cht-date-sep"><span>{{ dateSep(msg.created_at) }}</span></div>

            <div class="cht-msg" :class="{ mine: isMe(msg), theirs: !isMe(msg) }">
              <user-avatar v-if="!isMe(msg) && showSenderAvatar(i)" :src="msg.sender_avatar || otherAvatar" :name="msg.sender_name || otherName" size="sm" class="cht-msg-avatar" />
              <div v-else-if="!isMe(msg)" class="cht-msg-avatar-placeholder"></div>
              <div class="cht-bubble" :class="{ mine: isMe(msg) }"
                   @contextmenu.prevent="showMsgMenu($event, msg, i)"
                   @touchstart="touchHold(msg, i, $event)" @touchend="cancelHold" @touchmove="cancelHold">

                <!-- Deleted message -->
                <div v-if="isDeleted(msg)" class="cht-deleted">
                  <i class="mdi mdi-cancel"></i> {{ isMe(msg) ? 'You deleted this message' : 'This message was deleted' }}
                </div>
                <template v-else>

                  <!-- Listing Card -->
                  <div v-if="isListingCard(msg)" class="cht-listing-card" @click="goToListing(parseCard(msg))">
                    <img v-if="parseCard(msg).photo" :src="pu(parseCard(msg).photo)" class="cht-lc-img" />
                    <div class="cht-lc-info">
                      <div class="cht-lc-title">{{ parseCard(msg).title }}</div>
                      <div class="cht-lc-price">KES {{ fmtN(parseCard(msg).price) }}</div>
                      <div v-if="parseCard(msg).species" class="cht-lc-species">{{ parseCard(msg).species }}</div>
                    </div>
                    <i class="mdi mdi-chevron-right cht-lc-arrow"></i>
                  </div>
                  <div v-if="isListingCard(msg) && getCardText(msg)" class="cht-text mt-2">{{ getCardText(msg) }}</div>

                  <!-- Image -->
                  <div v-if="!isListingCard(msg) && (msg.message_type === 'image' || msg.message_type === 'photo') && msg.file_url" class="cht-img-wrap">
                    <img :src="pu(msg.file_url)" @click="openImage(msg.file_url)" />
                  </div>

                  <!-- Video -->
                  <div v-if="msg.message_type === 'video' && msg.file_url" class="cht-video-wrap">
                    <video :src="pu(msg.file_url)" controls preload="metadata" playsinline class="cht-video"></video>
                  </div>

                  <!-- Voice Note -->
                  <div v-if="msg.message_type === 'voice' || msg.message_type === 'voice_note'" class="cht-voice">
                    <button class="cht-voice-play" @click="playVoice(msg)">
                      <i class="mdi" :class="playingId === msg.id ? 'mdi-pause' : 'mdi-play'"></i>
                    </button>
                    <div class="cht-voice-wave"></div>
                    <span class="cht-voice-dur">{{ msg.duration || '0:00' }}</span>
                  </div>

                  <!-- Document -->
                  <div v-if="(msg.message_type === 'document' || msg.message_type === 'file') && msg.file_url" class="cht-doc" @click="openDoc(msg.file_url, msg.file_name)">
                    <i class="mdi mdi-file-document-outline cht-doc-icon"></i>
                    <div class="cht-doc-info">
                      <div class="cht-doc-name">{{ msg.file_name || 'Document' }}</div>
                      <div class="cht-doc-size">{{ msg.file_size || 'Tap to open' }}</div>
                    </div>
                    <i class="mdi mdi-download cht-doc-dl"></i>
                  </div>

                  <!-- Location -->
                  <div v-if="msg.message_type === 'location'" class="cht-loc">
                    <div class="cht-loc-map">
                      <iframe v-if="parseLoc(msg).lat" :src="'https://www.openstreetmap.org/export/embed.html?bbox=' + (parseLoc(msg).lng-0.005) + ',' + (parseLoc(msg).lat-0.003) + ',' + (parseLoc(msg).lng+0.005) + ',' + (parseLoc(msg).lat+0.003) + '&layer=mapnik&marker=' + parseLoc(msg).lat + ',' + parseLoc(msg).lng" class="cht-loc-iframe" frameborder="0" scrolling="no"></iframe>
                    </div>
                    <div class="cht-loc-footer">
                      <div class="cht-loc-info">
                        <div class="cht-loc-label">{{ getLocName(msg) }}</div>
                        <div class="cht-loc-coords">{{ getLocCoords(msg) }}</div>
                      </div>
                      <button class="cht-loc-btn" @click="openMap(msg)"><i class="mdi mdi-google-maps"></i> Open Map</button>
                    </div>
                  </div>

                  <!-- Text / Caption -->
                  <div v-if="!isListingCard(msg) && msg.content && !isJsonContent(msg.content) && !isTextOnlyHidden(msg.message_type)" class="cht-text" v-html="linkify(msg.content)"></div>

                </template>
                <div class="cht-meta">
                  <span v-if="msg.is_edited === '1' || msg.is_edited === 1" class="cht-edited">edited</span>
                  <span>{{ fmtTime(msg.created_at) }}</span>
                  <i v-if="isMe(msg)" class="mdi" :class="msg._pending ? 'mdi-check cht-pending-icon' : 'mdi-check-all cht-read-icon'"></i>
                </div>
              </div>
            </div>
          </template>

          <div v-if="sortedMessages.length === 0" class="text-center py-5">
            <i class="mdi mdi-message-plus-outline" style="font-size:40px;color:var(--fm-s,#2A2520);"></i>
            <p style="color:var(--fm-s,#6B6356);font-size:13px;margin-top:8px;">Start the conversation</p>
          </div>
        </template>
      </div>

      <!-- EMOJI PICKER -->
      <div v-if="showEmoji" class="cht-emoji-panel">
        <div class="cht-emoji-tabs">
          <button v-for="(cat, ci) in emojiCategories" :key="ci" :class="{ active: emojiTab === ci }" @click="emojiTab = ci">{{ cat.icon }}</button>
        </div>
        <div class="cht-emoji-grid">
          <span v-for="(e, ei) in emojiCategories[emojiTab].emojis" :key="ei" class="cht-emoji-item" @click="insertEmoji(e)">{{ e }}</span>
        </div>
      </div>

      <!-- ATTACHMENT MENU -->
      <div v-if="showAttach" class="cht-attach-menu">
        <div class="cht-attach-option" @click="pickFile('image')"><div class="cht-ao-icon" style="background:rgba(80,200,120,0.15);color:#50C878;"><i class="mdi mdi-image"></i></div><span>Photo</span></div>
        <div class="cht-attach-option" @click="pickFile('video')"><div class="cht-ao-icon" style="background:rgba(241,196,15,0.15);color:#f1c40f;"><i class="mdi mdi-video"></i></div><span>Video</span></div>
        <div class="cht-attach-option" @click="pickFile('camera')"><div class="cht-ao-icon" style="background:rgba(80,165,241,0.15);color:#50a5f1;"><i class="mdi mdi-camera"></i></div><span>Camera</span></div>
        <div class="cht-attach-option" @click="pickFile('document')"><div class="cht-ao-icon" style="background:rgba(155,89,182,0.15);color:#9b59b6;"><i class="mdi mdi-file-document"></i></div><span>Document</span></div>
        <div class="cht-attach-option" @click="shareLocation"><div class="cht-ao-icon" style="background:rgba(255,107,107,0.15);color:#FF6B6B;"><i class="mdi mdi-map-marker"></i></div><span>Location</span></div>
      </div>

      <!-- RECORDING INDICATOR -->
      <div v-if="recording" class="cht-recording-bar">
        <div class="container d-flex align-items-center gap-3">
          <div class="cht-rec-dot"></div>
          <span style="color:#FF6B6B;font-size:13px;font-weight:600;">Recording... {{ recTime }}</span>
          <button class="ms-auto btn btn-link text-danger p-0" @click="cancelRecording"><i class="mdi mdi-delete" style="font-size:22px;"></i></button>
          <button class="btn btn-link p-0" @click="stopRecording" style="color:#50C878;"><i class="mdi mdi-send" style="font-size:22px;"></i></button>
        </div>
      </div>

      <!-- FILE PREVIEW -->
      <div v-if="fileToSend && !recording" class="cht-preview-bar">
        <div class="container d-flex align-items-center gap-2">
          <img v-if="filePreview" :src="filePreview" class="cht-file-thumb" />
          <i v-else class="mdi mdi-file-document-outline" style="font-size:28px;color:#BFA164;"></i>
          <div class="flex-fill" style="min-width:0;">
            <div style="color:var(--fm-h,#F5F0E8);font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ fileToSend.name }}</div>
            <div style="color:#4A4540;font-size:10px;">{{ formatSize(fileToSend.size) }}</div>
          </div>
          <button class="btn btn-link text-danger p-0" @click="clearFile"><i class="mdi mdi-close" style="font-size:20px;"></i></button>
        </div>
      </div>

      <!-- INPUT BAR -->
      <div v-if="!recording && !editingMsg" class="cht-input-bar">
        <div class="container d-flex align-items-end gap-2">
          <button class="cht-icon-btn" @click="showEmoji = !showEmoji; showAttach = false"><i class="mdi" :class="showEmoji ? 'mdi-keyboard' : 'mdi-emoticon-outline'"></i></button>
          <div class="flex-fill" style="position:relative;">
            <textarea v-model="newMsg" class="cht-input" rows="1" placeholder="Type a message..." @keydown.enter.exact.prevent="send" @input="autoGrow" @focus="showEmoji = false" ref="msgInput"></textarea>
          </div>
          <button class="cht-icon-btn" @click="showAttach = !showAttach; showEmoji = false"><i class="mdi mdi-paperclip"></i></button>
          <button v-if="newMsg.trim() || fileToSend" class="cht-send-btn" @click="send" :disabled="sending">
            <i class="mdi mdi-send" v-if="!sending"></i>
            <span v-else class="spinner-border spinner-border-sm"></span>
          </button>
          <button v-else class="cht-mic-btn" @click="startRecording"><i class="mdi mdi-microphone"></i></button>
        </div>
      </div>

      <!-- LOCATION PICKER -->
      <div v-if="showLocPicker" class="cht-locpicker">
        <div class="cht-lp-header">
          <button class="cht-back-btn" @click="showLocPicker = false"><i class="mdi mdi-arrow-left"></i></button>
          <span style="color:var(--fm-h,#F5F0E8);font-size:15px;font-weight:600;">Share Location</span>
        </div>
        <div class="cht-lp-search">
          <i class="mdi mdi-magnify" style="color:var(--fm-s,#6B6356);font-size:20px;"></i>
          <input v-model="locQuery" class="cht-lp-input" placeholder="Search for a place..." @input="debounceLocSearch" ref="locSearchInput" />
          <button v-if="locQuery" class="btn btn-link p-0" @click="locQuery = ''; locResults = []"><i class="mdi mdi-close" style="color:var(--fm-s,#6B6356);"></i></button>
        </div>
        <!-- Current Location -->
        <div class="cht-lp-option" @click="shareCurrentLocation">
          <div class="cht-lp-icon" style="background:rgba(80,200,120,0.15);color:#50C878;"><i class="mdi mdi-crosshairs-gps"></i></div>
          <div>
            <div style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:600;">Send your current location</div>
            <div style="color:var(--fm-s,#6B6356);font-size:11px;">Share your live GPS position</div>
          </div>
          <span v-if="locGettingGps" class="spinner-border spinner-border-sm text-warning ms-auto"></span>
        </div>
        <!-- Search Results -->
        <div v-if="locSearching" class="text-center py-3"><span class="spinner-border spinner-border-sm text-warning"></span></div>
        <div v-for="(place, pi) in locResults" :key="pi" class="cht-lp-option" @click="sharePlace(place)">
          <div class="cht-lp-icon" style="background:rgba(255,107,107,0.12);color:#FF6B6B;"><i class="mdi mdi-map-marker"></i></div>
          <div style="min-width:0;flex:1;">
            <div style="color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ place.name }}</div>
            <div style="color:var(--fm-s,#6B6356);font-size:11px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ place.address }}</div>
          </div>
        </div>
        <!-- Popular / Nearby suggestions -->
        <div v-if="!locQuery && locResults.length === 0" class="px-3 pt-3">
          <div style="color:var(--fm-s,#6B6356);font-size:11px;font-weight:600;text-transform:uppercase;margin-bottom:8px;">Quick Share</div>
          <div class="cht-lp-option" @click="shareCustom('Home')">
            <div class="cht-lp-icon" style="background:rgba(191,161,100,0.12);color:#BFA164;"><i class="mdi mdi-home"></i></div>
            <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">Home</div>
          </div>
          <div class="cht-lp-option" @click="shareCustom('Meeting Point')">
            <div class="cht-lp-icon" style="background:rgba(155,89,182,0.12);color:#9b59b6;"><i class="mdi mdi-handshake"></i></div>
            <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">Meeting Point</div>
          </div>
          <div class="cht-lp-option" @click="shareCustom('Collection Point')">
            <div class="cht-lp-icon" style="background:rgba(80,165,241,0.12);color:#50a5f1;"><i class="mdi mdi-package-variant-closed"></i></div>
            <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">Collection Point</div>
          </div>
        </div>
      </div>

      <!-- MESSAGE CONTEXT MENU -->
      <div v-if="msgMenu" class="cht-menu-overlay" @click="msgMenu = null">
        <div class="cht-menu" :style="{ top: msgMenu.y + 'px', left: msgMenu.x + 'px' }">
          <div class="cht-menu-item" @click="copyMsg"><i class="mdi mdi-content-copy"></i> Copy</div>
          <div v-if="msgMenu.mine && canEdit(msgMenu.msg)" class="cht-menu-item" @click="editMsg"><i class="mdi mdi-pencil"></i> Edit</div>
          <div v-if="msgMenu.mine" class="cht-menu-item cht-menu-danger" @click="deleteForEveryone"><i class="mdi mdi-delete"></i> Delete for everyone</div>
          <div class="cht-menu-item cht-menu-danger" @click="deleteForMe"><i class="mdi mdi-eye-off"></i> Delete for me</div>
        </div>
      </div>

      <!-- EDIT BAR -->
      <div v-if="editingMsg" class="cht-edit-bar">
        <div class="container">
          <div class="d-flex align-items-center gap-2 mb-2">
            <i class="mdi mdi-pencil" style="color:#BFA164;"></i>
            <span style="color:#BFA164;font-size:12px;font-weight:600;">Editing message</span>
            <button class="ms-auto btn btn-link text-danger p-0" @click="cancelEdit"><i class="mdi mdi-close"></i></button>
          </div>
          <div class="d-flex align-items-end gap-2">
            <textarea v-model="editText" class="cht-input flex-fill" rows="1" @keydown.enter.exact.prevent="saveEdit" ref="editInput"></textarea>
            <button class="cht-send-btn" @click="saveEdit" :disabled="!editText.trim()"><i class="mdi mdi-check"></i></button>
          </div>
        </div>
      </div>

      <!-- DOCUMENT VIEWER -->
      <div v-if="docViewUrl" class="cht-docview">
        <div class="cht-docview-topbar">
          <div class="d-flex align-items-center gap-2 flex-fill" style="min-width:0;">
            <i class="mdi mdi-file-document-outline" style="font-size:22px;color:#BFA164;"></i>
            <span class="cht-docview-name">{{ docViewName }}</span>
          </div>
          <div class="d-flex align-items-center gap-2">
            <button class="cht-docview-btn" @click="downloadDoc" title="Download"><i class="mdi mdi-download"></i></button>
            <button class="cht-docview-btn" @click="openDocExternal" title="Open in new tab"><i class="mdi mdi-open-in-new"></i></button>
            <button class="cht-docview-btn cht-docview-close" @click="docViewUrl = null" title="Close"><i class="mdi mdi-close"></i></button>
          </div>
        </div>
        <div class="cht-docview-body">
          <iframe v-if="isDocPreviewable" :src="docViewUrl" class="cht-docview-iframe"></iframe>
          <div v-else class="cht-docview-fallback">
            <i class="mdi mdi-file-document-outline" style="font-size:64px;color:#BFA164;"></i>
            <p style="color:var(--fm-h,#F5F0E8);font-size:16px;font-weight:600;margin-top:12px;">{{ docViewName }}</p>
            <p style="color:var(--fm-s,#6B6356);font-size:13px;">This file type can't be previewed in chat</p>
            <button class="cht-docview-dl-btn" @click="downloadDoc"><i class="mdi mdi-download me-2"></i>Download File</button>
            <button class="cht-docview-dl-btn" style="background:transparent;border:1px solid var(--fm-br,#2A2520);color:var(--fm-m,#A09888);" @click="openDocExternal"><i class="mdi mdi-open-in-new me-2"></i>Open in Browser</button>
          </div>
        </div>
      </div>

      <!-- IMAGE LIGHTBOX -->
      <div v-if="lightboxUrl" class="cht-lightbox" @click.self="lightboxUrl = null">
        <button class="cht-lb-close" @click="lightboxUrl = null"><i class="mdi mdi-close"></i></button>
        <div class="cht-lb-actions">
          <button @click="zoomIn"><i class="mdi mdi-magnify-plus-outline"></i></button>
          <button @click="zoomOut"><i class="mdi mdi-magnify-minus-outline"></i></button>
          <button @click="resetZoom"><i class="mdi mdi-fit-to-screen-outline"></i></button>
          <button @click="downloadImage"><i class="mdi mdi-download"></i></button>
        </div>
        <div class="cht-lb-container" ref="lbContainer"
             @wheel.prevent="onWheel"
             @mousedown="dragStart" @mousemove="dragMove" @mouseup="dragEnd" @mouseleave="dragEnd"
             @touchstart="touchStart" @touchmove.prevent="touchMove" @touchend="touchEnd">
          <img :src="lightboxUrl" class="cht-lb-img" :style="lbImgStyle" @load="resetZoom" draggable="false" />
        </div>
      </div>

      <!-- Hidden file inputs -->
      <input type="file" ref="imageInput" accept="image/*" @change="onFileSelected" hidden />
      <input type="file" ref="cameraInput" accept="image/*" capture="environment" @change="onFileSelected" hidden />
      <input type="file" ref="videoInput" accept="video/*" @change="onVideoSelected" hidden />
      <input type="file" ref="docInput" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip" @change="onDocSelected" hidden />
    </div>
  </div>
</template>
<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
html[data-theme="light"] body {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}
html[data-theme="dark"] body {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}
html[data-theme="light"] .cht-root,
html[data-theme="light"] .cht-layout-wrapper,
html[data-theme="light"] .cht-root #layout-wrapper,
html[data-theme="light"] .cht-root .cht-body {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}
html[data-theme="dark"] .cht-root,
html[data-theme="dark"] .cht-layout-wrapper,
html[data-theme="dark"] .cht-root #layout-wrapper,
html[data-theme="dark"] .cht-root .cht-body {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}
html[data-theme="light"] .cht-topbar,
html[data-theme="light"] .cht-input-bar,
html[data-theme="light"] .cht-preview-bar,
html[data-theme="light"] .cht-recording-bar,
html[data-theme="light"] .cht-emoji-panel,
html[data-theme="light"] .cht-attach-menu,
html[data-theme="light"] .cht-locpicker,
html[data-theme="light"] .cht-docview-topbar {
  background:#FFFFFF!important;
  border-color:#E8E2DA!important;
}
html[data-theme="light"] .cht-bubble:not(.mine),
html[data-theme="light"] .cht-date-sep span {
  background:#F0EBE4!important;
  color:#555!important;
}
html[data-theme="light"] .cht-preview-bar,
html[data-theme="light"] .cht-recording-bar {
  box-shadow:0 -1px 0 #E8E2DA inset;
}
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>
<style scoped>
.cht-root,.cht-layout-wrapper{background:var(--fm-bg2,#0F0F0F)!important;min-height:100vh;display:flex;flex-direction:column;color:var(--fm-h,#F5F0E8)}
.cht-topbar{position:fixed;top:0;left:0;right:0;z-index:100;padding:10px 0;background:var(--fm-bg1,#141414)!important;backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.cht-back-btn{background:none;border:none;color:var(--fm-s,#6B6356);font-size:22px;padding:0;cursor:pointer}
.cht-back-btn:hover{color:#BFA164}
.cht-avatar-sm{width:36px;height:36px;border-radius:50%;background:rgba(191,161,100,0.15);display:flex;align-items:center;justify-content:center;color:#BFA164;font-size:12px;font-weight:700;flex-shrink:0;position:relative}
.cht-avatar-sm.cht-online::after{content:'';position:absolute;bottom:0;right:0;width:10px;height:10px;border-radius:50%;background:#50C878;border:2px solid #0A0A0A}
.cht-body{flex:1;overflow-y:auto;padding:70px 0 110px;max-width:700px;margin:0 auto;width:100%;padding-left:12px;padding-right:12px;background:transparent!important}

/* Date separator */
.cht-date-sep{text-align:center;margin:16px 0 8px}
.cht-date-sep span{background:var(--fm-bg3,#1E1E1E);color:var(--fm-s,#6B6356);font-size:11px;padding:4px 12px;border-radius:8px}

/* Messages */
.cht-from-notif-pill{display:inline-flex;align-items:center;background:rgba(108,180,238,.12);color:#6CB4EE;border:1px solid rgba(108,180,238,.4);border-radius:12px;padding:2px 8px;font-size:10px;cursor:pointer;margin-left:6px}
.cht-from-notif-pill:hover{background:rgba(108,180,238,.2)}
.cht-msg{display:flex;margin-bottom:4px;align-items:flex-end;gap:6px}
.cht-msg.mine{justify-content:flex-end}
.cht-msg.theirs{justify-content:flex-start}
.cht-msg-avatar{flex-shrink:0;align-self:flex-end;margin-bottom:2px}
.cht-msg-avatar-placeholder{width:32px;flex-shrink:0}
.cht-avatar-wrap{position:relative;flex-shrink:0}
.cht-avatar-wrap.cht-online::after{content:'';position:absolute;bottom:0;right:0;width:10px;height:10px;border-radius:50%;background:#50C878;border:2px solid var(--fm-bg2,#0F0F0F)}
.cht-bubble{max-width:78%;padding:8px 12px;border-radius:12px;position:relative}
.cht-bubble.mine{background:#1A2E1A;border-bottom-right-radius:3px}
.cht-bubble:not(.mine){background:#1A1A1A;border-bottom-left-radius:3px}
.cht-text{font-size:14px;color:var(--fm-h,#F5F0E8);line-height:1.5;word-wrap:break-word}
.cht-text a{color:#50a5f1;text-decoration:underline}
.cht-text strong{color:#F5F0E8;font-weight:700}
.cht-text em{font-style:italic}
.cht-text .cht-h1{font-weight:700;font-size:15px;color:#BFA164;margin:10px 0 4px}
.cht-text .cht-h2{font-weight:700;font-size:13px;color:#BFA164;margin:12px 0 4px;text-transform:uppercase;letter-spacing:0.5px}
.cht-text .cht-h3{font-weight:700;font-size:13px;color:#BFA164;margin:8px 0 3px}
.cht-text .cht-num{color:#BFA164;font-weight:700}
.cht-text .cht-bullet{color:#BFA164}
.cht-text .cht-bullet-sub{color:#6B6356}
.cht-text .cht-code{background:rgba(191,161,100,0.12);padding:1px 5px;border-radius:3px;font-size:12px;font-family:monospace}
.cht-meta{font-size:10px;color:#4A4540;margin-top:2px;text-align:right;display:flex;align-items:center;justify-content:flex-end;gap:3px}
.cht-read-icon{font-size:14px;color:#50a5f1}
.cht-pending-icon{font-size:14px;color:#9aa4b2}

/* Images */
.cht-img-wrap{margin-bottom:4px}
.cht-img-wrap img{max-width:280px;max-height:300px;border-radius:8px;cursor:pointer;display:block}

/* Video */
.cht-video-wrap{margin-bottom:4px}
.cht-video{max-width:280px;max-height:300px;border-radius:8px;display:block;background:#000}

/* Listing Card */
.cht-listing-card{display:flex;align-items:center;gap:10px;background:rgba(191,161,100,0.08);border:1px solid rgba(191,161,100,0.2);border-radius:10px;padding:10px;cursor:pointer;transition:border-color .2s}
.cht-listing-card:hover{border-color:#BFA164}
.cht-lc-img{width:52px;height:52px;border-radius:8px;object-fit:cover;flex-shrink:0}
.cht-lc-info{flex:1;min-width:0}
.cht-lc-title{font-size:13px;font-weight:600;color:var(--fm-h,#F5F0E8);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cht-lc-price{font-size:13px;font-weight:700;color:#BFA164;margin-top:1px}
.cht-lc-species{font-size:10px;color:var(--fm-s,#6B6356);margin-top:1px}
.cht-lc-arrow{color:var(--fm-s,#6B6356);font-size:18px;flex-shrink:0}

/* Voice */
.cht-voice{display:flex;align-items:center;gap:10px;min-width:180px}
.cht-voice-play{width:34px;height:34px;border-radius:50%;background:#BFA164;border:none;display:flex;align-items:center;justify-content:center;color:#0A0A0A;font-size:18px;cursor:pointer;flex-shrink:0}
.cht-voice-wave{flex:1;height:24px;background:linear-gradient(90deg,#BFA164 0%,#BFA164 30%,#4A4540 30%);border-radius:4px;opacity:0.5;background-size:100% 100%;background-image:repeating-linear-gradient(90deg,#BFA164 0px,#BFA164 2px,transparent 2px,transparent 4px)}
.cht-voice-dur{font-size:11px;color:var(--fm-s,#6B6356);flex-shrink:0}

/* Document */
.cht-doc{display:flex;align-items:center;gap:10px;padding:8px;background:rgba(155,89,182,0.08);border:1px solid rgba(155,89,182,0.2);border-radius:8px;cursor:pointer;min-width:200px}
.cht-doc:hover{border-color:#9b59b6}
.cht-doc-icon{font-size:32px;color:#9b59b6;flex-shrink:0}
.cht-doc-info{flex:1;min-width:0}
.cht-doc-name{font-size:12px;font-weight:600;color:var(--fm-h,#F5F0E8);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cht-doc-size{font-size:10px;color:var(--fm-s,#6B6356)}
.cht-doc-dl{font-size:20px;color:var(--fm-s,#6B6356);flex-shrink:0}

/* Location */
.cht-loc{border-radius:10px;overflow:hidden;min-width:240px}
.cht-loc-map{position:relative;width:260px;height:140px;background:#1E2D1E;overflow:hidden}
.cht-loc-iframe{width:100%;height:100%;border:none;pointer-events:none}
.cht-loc-footer{display:flex;align-items:center;gap:8px;padding:8px 10px;background:rgba(30,45,30,0.5)}
.cht-loc-info{flex:1;min-width:0}
.cht-loc-label{font-size:12px;font-weight:600;color:var(--fm-h,#F5F0E8)}
.cht-loc-coords{font-size:10px;color:var(--fm-s,#6B6356);margin-top:1px}
.cht-loc-btn{background:rgba(80,165,241,0.15);border:1px solid rgba(80,165,241,0.3);color:#50a5f1;font-size:11px;font-weight:600;padding:5px 10px;border-radius:6px;cursor:pointer;white-space:nowrap;display:flex;align-items:center;gap:4px;transition:all .2s}
.cht-loc-btn:hover{background:rgba(80,165,241,0.25);border-color:#50a5f1}

/* Emoji panel */
.cht-emoji-panel{position:fixed;bottom:60px;left:0;right:0;z-index:90;background:#111;border-top:1px solid #1E1E1E;max-height:260px;overflow:hidden}
.cht-emoji-tabs{display:flex;gap:2px;padding:6px 12px;border-bottom:1px solid var(--fm-br,#1E1E1E);overflow-x:auto}
.cht-emoji-tabs button{background:none;border:none;font-size:18px;padding:4px 8px;border-radius:6px;cursor:pointer}
.cht-emoji-tabs button.active{background:rgba(191,161,100,0.15)}
.cht-emoji-grid{display:grid;grid-template-columns:repeat(8,1fr);gap:2px;padding:8px 12px;overflow-y:auto;max-height:200px}
.cht-emoji-item{font-size:24px;text-align:center;cursor:pointer;padding:4px;border-radius:6px;transition:background .15s}
.cht-emoji-item:hover{background:rgba(191,161,100,0.1)}

/* Attach menu */
.cht-attach-menu{position:fixed;bottom:60px;left:0;right:0;z-index:90;background:#111;border-top:1px solid #1E1E1E;padding:16px;display:flex;justify-content:center;gap:24px}
.cht-attach-option{display:flex;flex-direction:column;align-items:center;gap:6px;cursor:pointer}
.cht-attach-option span{font-size:11px;color:var(--fm-s,#6B6356)}
.cht-ao-icon{width:48px;height:48px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:22px;transition:transform .15s}
.cht-ao-icon:hover{transform:scale(1.1)}

/* Recording */
.cht-recording-bar{position:fixed;bottom:0;left:0;right:0;z-index:100;padding:14px 0;background:rgba(10,10,10,0.97);border-top:1px solid #2A1A1A}
.cht-rec-dot{width:10px;height:10px;border-radius:50%;background:#FF6B6B;animation:recPulse 1s infinite}
@keyframes recPulse{0%,100%{opacity:1}50%{opacity:0.3}}

/* Preview */
.cht-preview-bar{position:fixed;bottom:60px;left:0;right:0;z-index:80;padding:10px 0;background:#111;border-top:1px solid #1E1E1E}
.cht-file-thumb{width:44px;height:44px;border-radius:6px;object-fit:cover;flex-shrink:0}

/* Location Picker */
.cht-locpicker{position:fixed;top:0;left:0;right:0;bottom:0;z-index:200;background:#0A0A0A;display:flex;flex-direction:column;overflow-y:auto}
.cht-lp-header{display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--fm-br,#1E1E1E)}
.cht-lp-search{display:flex;align-items:center;gap:8px;margin:12px 16px;padding:10px 14px;background:#1A1A1A;border:1px solid var(--fm-br,#2A2520);border-radius:12px}
.cht-lp-search:focus-within{border-color:#BFA164}
.cht-lp-input{flex:1;background:none;border:none;outline:none;color:var(--fm-h,#F5F0E8);font-size:14px}
.cht-lp-input::placeholder{color:#4A4540}
.cht-lp-option{display:flex;align-items:center;gap:12px;padding:12px 16px;cursor:pointer;transition:background .15s}
.cht-lp-option:hover{background:rgba(191,161,100,0.05)}
.cht-lp-icon{width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0}

/* Deleted message */
.cht-deleted{font-size:13px;color:var(--fm-s,#6B6356);font-style:italic;display:flex;align-items:center;gap:4px}
.cht-edited{font-style:italic;opacity:0.7}

/* Context menu */
.cht-menu-overlay{position:fixed;top:0;left:0;right:0;bottom:0;z-index:9000}
.cht-menu{position:fixed;background:var(--fm-bg3,#1E1E1E);border:1px solid var(--fm-br,#2A2520);border-radius:10px;padding:4px 0;min-width:180px;box-shadow:0 8px 30px rgba(0,0,0,0.5);z-index:9001;animation:menuFade .15s ease}
@keyframes menuFade{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}
.cht-menu-item{display:flex;align-items:center;gap:10px;padding:10px 16px;font-size:13px;color:var(--fm-h,#F5F0E8);cursor:pointer;transition:background .15s}
.cht-menu-item:hover{background:rgba(191,161,100,0.1)}
.cht-menu-item i{font-size:18px;color:var(--fm-s,#6B6356);width:20px;text-align:center}
.cht-menu-danger{color:#FF6B6B}
.cht-menu-danger i{color:#FF6B6B}

/* Edit bar */
.cht-edit-bar{position:fixed;bottom:0;left:0;right:0;z-index:100;padding:10px 0;background:rgba(10,10,10,0.97);border-top:1px solid #BFA164}

/* Document Viewer */
.cht-docview{position:fixed;top:0;left:0;right:0;bottom:0;z-index:9998;background:#0A0A0A;display:flex;flex-direction:column}
.cht-docview-topbar{display:flex;align-items:center;padding:10px 16px;background:#111;border-bottom:1px solid var(--fm-br,#1E1E1E);gap:8px}
.cht-docview-name{color:var(--fm-h,#F5F0E8);font-size:13px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cht-docview-btn{background:rgba(255,255,255,0.06);border:none;color:var(--fm-m,#A09888);font-size:20px;width:36px;height:36px;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .2s}
.cht-docview-btn:hover{background:rgba(255,255,255,0.12);color:var(--fm-h,#F5F0E8)}
.cht-docview-close:hover{color:#FF6B6B}
.cht-docview-body{flex:1;overflow:hidden;display:flex;align-items:center;justify-content:center}
.cht-docview-iframe{width:100%;height:100%;border:none;background:#fff}
.cht-docview-fallback{text-align:center;padding:40px 20px}
.cht-docview-dl-btn{display:flex;align-items:center;justify-content:center;margin:10px auto 0;padding:10px 24px;background:#BFA164;color:#0A0A0A;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s}
.cht-docview-dl-btn:hover{background:#d4b96e}

/* Lightbox */
.cht-lightbox{position:fixed;top:0;left:0;right:0;bottom:0;z-index:9999;background:rgba(0,0,0,0.95);display:flex;flex-direction:column;align-items:center;justify-content:center}
.cht-lb-close{position:absolute;top:12px;right:12px;z-index:10;background:rgba(255,255,255,0.1);border:none;color:#fff;font-size:28px;width:44px;height:44px;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .2s}
.cht-lb-close:hover{background:rgba(255,255,255,0.2)}
.cht-lb-actions{position:absolute;bottom:20px;left:50%;transform:translateX(-50%);z-index:10;display:flex;gap:8px;background:rgba(30,30,30,0.9);padding:8px 12px;border-radius:24px}
.cht-lb-actions button{background:none;border:none;color:#fff;font-size:22px;width:40px;height:40px;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .2s}
.cht-lb-actions button:hover{background:rgba(255,255,255,0.15)}
.cht-lb-container{width:100%;height:100%;overflow:hidden;cursor:grab;display:flex;align-items:center;justify-content:center;touch-action:none}
.cht-lb-container:active{cursor:grabbing}
.cht-lb-img{max-width:90vw;max-height:85vh;object-fit:contain;transition:transform 0.15s ease-out;user-select:none;-webkit-user-drag:none}

/* Input bar */
.cht-input-bar{position:fixed;bottom:0;left:0;right:0;z-index:100;padding:8px 0;background:rgba(10,10,10,0.97);border-top:1px solid #1E1E1E}
.cht-input{width:100%;background:#1A1A1A;border:1px solid var(--fm-br,#2A2520);border-radius:20px;padding:9px 14px;color:var(--fm-h,#F5F0E8);font-size:14px;resize:none;max-height:100px;overflow-y:auto}
.cht-input:focus{border-color:#BFA164;outline:none}
.cht-input::placeholder{color:#4A4540}
.cht-icon-btn{width:38px;height:38px;border-radius:50%;background:none;border:none;display:flex;align-items:center;justify-content:center;color:var(--fm-s,#6B6356);font-size:22px;flex-shrink:0;cursor:pointer;transition:color .2s}
.cht-icon-btn:hover{color:#BFA164}
.cht-send-btn{width:38px;height:38px;border-radius:50%;background:#BFA164;border:none;display:flex;align-items:center;justify-content:center;color:#0A0A0A;font-size:18px;flex-shrink:0;cursor:pointer;transition:all .15s}
.cht-send-btn:disabled{opacity:0.4;cursor:not-allowed}
.cht-mic-btn{width:38px;height:38px;border-radius:50%;background:none;border:none;display:flex;align-items:center;justify-content:center;color:var(--fm-s,#6B6356);font-size:22px;flex-shrink:0;cursor:pointer;transition:color .2s}
.cht-mic-btn:hover{color:#BFA164}
</style>

<style>
html[data-theme="light"] .cht-root .cht-msg.mine .cht-bubble {
  background: linear-gradient(135deg, #e8f5e9, #dff0df) !important;
  border: 1px solid #cfe4cf !important;
}
html[data-theme="light"] .cht-root .cht-msg.theirs .cht-bubble {
  background: #ffffff !important;
  border: 1px solid #e8e2da !important;
}
html[data-theme="light"] .cht-root .cht-msg.mine .cht-text,
html[data-theme="light"] .cht-root .cht-msg.mine .cht-meta,
html[data-theme="light"] .cht-root .cht-msg.mine .cht-edited,
html[data-theme="light"] .cht-root .cht-msg.mine .cht-lc-title,
html[data-theme="light"] .cht-root .cht-msg.mine .cht-lc-species,
html[data-theme="light"] .cht-root .cht-msg.mine .cht-lc-arrow,
html[data-theme="light"] .cht-root .cht-msg.mine .cht-doc-name,
html[data-theme="light"] .cht-root .cht-msg.mine .cht-doc-size,
html[data-theme="light"] .cht-root .cht-msg.mine .cht-loc-label,
html[data-theme="light"] .cht-root .cht-msg.mine .cht-loc-coords,
html[data-theme="light"] .cht-root .cht-msg.mine .cht-voice-dur,
html[data-theme="light"] .cht-root .cht-msg.mine span,
html[data-theme="light"] .cht-root .cht-msg.mine p,
html[data-theme="light"] .cht-root .cht-msg.mine div {
  color: #1a1a1a !important;
}
html[data-theme="light"] .cht-root .cht-msg.mine .cht-lc-price {
  color: #bfa164 !important;
}
html[data-theme="light"] .cht-root .cht-msg.mine .cht-read-icon {
  color: #50a5f1 !important;
}
html[data-theme="light"] .cht-root .cht-msg.mine .cht-listing-card {
  background: rgba(255,255,255,0.45) !important;
  border-color: #c7d8c7 !important;
}
html[data-theme="light"] .cht-root .cht-msg.theirs .cht-text,
html[data-theme="light"] .cht-root .cht-msg.theirs .cht-meta,
html[data-theme="light"] .cht-root .cht-msg.theirs .cht-edited,
html[data-theme="light"] .cht-root .cht-msg.theirs .cht-lc-title,
html[data-theme="light"] .cht-root .cht-msg.theirs .cht-doc-name,
html[data-theme="light"] .cht-root .cht-msg.theirs .cht-loc-label {
  color: #1a1a1a !important;
}
html[data-theme="light"] .cht-root .cht-msg.theirs .cht-lc-species,
html[data-theme="light"] .cht-root .cht-msg.theirs .cht-doc-size,
html[data-theme="light"] .cht-root .cht-msg.theirs .cht-loc-coords {
  color: #555 !important;
}
html[data-theme="light"] .cht-root .cht-input-bar {
  background: #ffffff !important;
  border-top: 1px solid #e8e2da !important;
}
html[data-theme="light"] .cht-root .cht-input {
  background: #ffffff !important;
  border: 1px solid #e0d8d0 !important;
  color: #1a1a1a !important;
}
html[data-theme="light"] .cht-root .cht-input::placeholder {
  color: #777 !important;
}
html[data-theme="light"] .cht-root .cht-icon-btn,
html[data-theme="light"] .cht-root .cht-mic-btn {
  color: #555 !important;
}
</style>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";
export default {
  name: "ChatThread",
  components: { UserAvatar },
  data() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
      themeObserver: null,

      loading: true, messages: [], otherName: '', otherAvatar: '', contextLabel: '', cameFromNotifications: false,
      newMsg: '', sending: false, fileToSend: null, filePreview: null,
      fileType: 'photo',
      page: 1, hasMore: true, loadingMore: false, pollTimer: null,
      conversationId: '', convData: null, destroyed: false,
      otherId: '', otherOnline: false, otherLastSeen: null, presenceTimer: null,
      showEmoji: false, showAttach: false,
      emojiTab: 0,
      // Voice
      recording: false, mediaRecorder: null, audioChunks: [], recTime: '0:00', recInterval: null, recStart: 0,
      playingId: null, currentAudio: null,
      // Lightbox
      lightboxUrl: null, lbScale: 1, lbX: 0, lbY: 0, lbDragging: false, lbDragStartX: 0, lbDragStartY: 0, lbLastX: 0, lbLastY: 0,
      // Touch pinch
      lbTouchDist: 0,
      // Doc viewer
      docViewUrl: null, docViewName: '',
      // Context menu
      msgMenu: null, holdTimer: null,
      // Edit
      editingMsg: null, editText: '',
      // Location picker
      showLocPicker: false, locQuery: '', locResults: [], locSearching: false, locGettingGps: false, locSearchTimer: null,
      emojiCategories: [
        { icon: '😀', emojis: ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😊','😇','🥰','😍','🤩','😘','😋','😛','🤔','🤫','🤭','😶','😏','😌','😴','🤤','😷','🤒','🤢','🤮','🥴','😵','🤯','🤠','🥳','😎','🤓','🧐','😤','😡','🤬','😈','💀','☠️','💩','🤡','👹','👺','👻','👽','🤖'] },
        { icon: '👋', emojis: ['👋','🤚','🖐️','✋','🖖','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','👇','☝️','👍','👎','✊','👊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏','💪','🦵','🦶'] },
        { icon: '❤️', emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','♥️','🔥','⭐','✨','🌟','💫','💥','💯','🎉','🎊','🏆','🥇','🏅'] },
        { icon: '🐄', emojis: ['🐄','🐮','🐂','🐃','🐷','🐖','🐗','🐽','🐑','🐏','🐐','🐪','🐫','🦙','🐘','🦏','🦛','🐭','🐁','🐀','🐹','🐰','🐇','🐿️','🦫','🦔','🦇','🐻','🐨','🐼','🦥','🐔','🐓','🐣','🐤','🐥','🐦','🦆','🦅','🦉','🦤','🐴','🐎','🦓'] },
        { icon: '🍎', emojis: ['🍎','🍐','🍊','🍋','🍌','🍉','🍇','🍓','🫐','🍈','🍒','🍑','🥭','🍍','🥥','🥝','🍅','🥑','🌽','🥕','🧅','🥔','🍠','🥩','🍗','🥚','🥛','🧈','🍞','🥐','🫓'] },
        { icon: '💰', emojis: ['💰','💵','💴','💶','💷','💸','💳','🧾','💹','📈','📉','🏦','🏧','💱','💲','✅','❌','⚠️','📌','📍','🔗','📎','📱','💻','📊','📋','📝','📞','📧','🔔','🔑','🔒'] }
      ]
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    sortedMessages() {
      return this.messages.slice().sort(function(a, b) {
        return new Date(a.created_at) - new Date(b.created_at);
      });
    },
    lbImgStyle() {
      return { transform: 'translate(' + this.lbX + 'px, ' + this.lbY + 'px) scale(' + this.lbScale + ')' };
    },
    isDocPreviewable() {
      if (!this.docViewName) return false;
      var ext = this.docViewName.split('.').pop().toLowerCase();
      return ['pdf','txt','csv','html','htm','jpg','jpeg','png','gif','webp','svg'].indexOf(ext) > -1;
    }
  },
  mounted() {
    this.syncTheme();
    this.themeObserver = new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    this.conversationId = this.$route.params.id;
    try {
      this.cameFromNotifications = sessionStorage.getItem('flockr_nav_from') === 'notifications';
      sessionStorage.removeItem('flockr_nav_from');
    } catch(e){}
    this.loadMessages();
    this.startPolling();
  },
  beforeUnmount() {
    this.destroyed = true;
    this.stopPolling();
    this.stopAudio();
    this.stopPresence();
    this.setMeOffline();
    clearTimeout(this.locSearchTimer);
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  beforeRouteLeave(to, from, next) { this.destroyed = true; this.stopPolling(); this.stopAudio(); this.stopPresence(); this.setMeOffline(); next(); },
  methods: {
    syncTheme() {
      var t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark';
      window.__flockrSetTheme(t);
      this.darkMode = t !== 'light';
      if (document.body) {
        document.body.setAttribute('data-sidebar', this.darkMode ? 'dark' : 'light');
        document.body.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        document.body.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
      var el = this.$el;
      if (el) {
        el.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        el.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
    },
    pu(p) { if (!p) return ''; if (p.startsWith('http') || p.startsWith('data:') || p.startsWith('blob:')) return p; return uploads + String(p).replace('uploads/', ''); },
    initials(n) { if (!n) return '?'; return n.split(' ').map(function(w) { return w.charAt(0); }).join('').substring(0, 2).toUpperCase(); },
    isMe(msg) { return String(msg.sender_id) === sessionStorage.getItem('userId'); },
    fmtN(v) { return v ? Number(v).toLocaleString() : '0'; },
    fmtTime(d) {
      if (!d) return '';
      var dt = new Date(d);
      return dt.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
    },
    fmtLastSeen(d) {
      if (!d) return '';
      var dt = new Date(d); var now = new Date();
      var diffMs = now - dt; var diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'just now';
      if (diffMin < 60) return diffMin + ' min ago';
      var diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return diffHr + 'h ago';
      if (dt.toDateString() === new Date(now - 86400000).toDateString()) return 'yesterday at ' + this.fmtTime(d);
      return dt.toLocaleDateString('en-KE', { day: 'numeric', month: 'short' }) + ' at ' + this.fmtTime(d);
    },
    dateSep(d) {
      if (!d) return '';
      var dt = new Date(d); var now = new Date();
      if (dt.toDateString() === now.toDateString()) return 'Today';
      var y = new Date(now); y.setDate(y.getDate() - 1);
      if (dt.toDateString() === y.toDateString()) return 'Yesterday';
      return dt.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    showDateSep(i) {
      if (i === 0) return true;
      var curr = this.sortedMessages[i]; var prev = this.sortedMessages[i - 1];
      return new Date(curr.created_at).toDateString() !== new Date(prev.created_at).toDateString();
    },
    isJsonContent(c) { return c && c.charAt(0) === '{' && c.indexOf('_type') > -1; },
    isSpecialType(t) { return ['voice','voice_note','location','document','file','image','photo','video'].indexOf(t) > -1; },
    isTextOnlyHidden(t) { return ['voice','voice_note','location'].indexOf(t) > -1; },
    isListingCard(msg) {
      if (msg.message_type === 'listing_card') return true;
      if (msg.content && msg.content.charAt(0) === '{') {
        try { var d = JSON.parse(msg.content); return d && d._type === 'listing_card'; } catch(e) {}
      }
      return false;
    },
    parseCard(msg) { try { return JSON.parse(msg.content) || {}; } catch(e) { return {}; } },
    getCardText(msg) { return this.parseCard(msg).text || ''; },
    goToListing(card) { if (card.listing_id) this.$router.push('/marketplace/listing/' + card.listing_id); },
    linkify(text) {
      if (!text) return '';
      var s = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      // Markdown headings (must run before bold so ## doesn't get partially-eaten)
      s = s.replace(/^###\s+(.+)$/gm, '<div class="cht-h3">$1</div>');
      s = s.replace(/^##\s+(.+)$/gm, '<div class="cht-h2">$1</div>');
      s = s.replace(/^#\s+(.+)$/gm, '<div class="cht-h1">$1</div>');
      // Bold + italic
      s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
      // Lists
      s = s.replace(/^\s{3,}[-•]\s+/gm, '&nbsp;&nbsp;&nbsp;<span class="cht-bullet-sub">◦</span> ');
      s = s.replace(/^(\d+)\.\s+/gm, '<span class="cht-num">$1.</span> ');
      s = s.replace(/^[-•]\s+/gm, '<span class="cht-bullet">•</span> ');
      // Inline code
      s = s.replace(/`([^`]+)`/g, '<code class="cht-code">$1</code>');
      // URLs
      s = s.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
      // Paragraph + line breaks
      s = s.replace(/\n\n+/g, '<br><br>');
      s = s.replace(/\n/g, '<br>');
      return s;
    },
    formatSize(b) {
      if (!b) return '';
      if (b < 1024) return b + ' B';
      if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
      return (b / 1048576).toFixed(1) + ' MB';
    },
    autoGrow() {
      var el = this.$refs.msgInput;
      if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 100) + 'px'; }
    },
    scrollToBottom() {
      var self = this;
      setTimeout(function() {
        if (self.$refs.chatBody) self.$refs.chatBody.scrollTop = self.$refs.chatBody.scrollHeight;
      }, 80);
    },

    // EMOJI
    insertEmoji(e) {
      this.newMsg += e;
      this.$nextTick(function() { if (this.$refs.msgInput) this.$refs.msgInput.focus(); }.bind(this));
    },

    // ATTACHMENTS
    pickFile(type) {
      this.showAttach = false;
      if (type === 'image') this.$refs.imageInput.click();
      else if (type === 'video') this.$refs.videoInput.click();
      else if (type === 'camera') this.$refs.cameraInput.click();
      else if (type === 'document') this.$refs.docInput.click();
    },
    onFileSelected(e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;
      e.target.value = '';
      var self = this;
      this.compressImage(file, 1280, 0.7).then(function(result) {
        self.fileToSend = result.file;
        self.fileType = 'photo';
        self.filePreview = result.dataUrl;
      });
    },
    compressImage(file, maxDim, quality) {
      return new Promise(function(resolve) {
        // Skip non-images or tiny files (<100KB)
        if (!file.type.startsWith('image/') || file.size < 100000) {
          var r = new FileReader();
          r.onload = function(ev) { resolve({ file: file, dataUrl: ev.target.result }); };
          r.readAsDataURL(file);
          return;
        }
        var img = new Image();
        var url = URL.createObjectURL(file);
        img.onload = function() {
          URL.revokeObjectURL(url);
          var w = img.width; var h = img.height;
          // Scale down if larger than maxDim
          if (w > maxDim || h > maxDim) {
            if (w > h) { h = Math.round(h * maxDim / w); w = maxDim; }
            else { w = Math.round(w * maxDim / h); h = maxDim; }
          }
          var canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(function(blob) {
            var compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
            var dataUrl = canvas.toDataURL('image/jpeg', quality);
            resolve({ file: compressed, dataUrl: dataUrl });
          }, 'image/jpeg', quality);
        };
        img.onerror = function() {
          URL.revokeObjectURL(url);
          var r = new FileReader();
          r.onload = function(ev) { resolve({ file: file, dataUrl: ev.target.result }); };
          r.readAsDataURL(file);
        };
        img.src = url;
      });
    },
    onDocSelected(e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;
      this.fileToSend = file;
      this.fileType = 'file';
      this.filePreview = null;
      e.target.value = '';
    },
    onVideoSelected(e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;
      this.fileToSend = file;
      this.fileType = 'video';
      this.filePreview = null;
      // Generate video thumbnail
      var video = document.createElement('video');
      video.preload = 'metadata';
      var self = this;
      video.onloadeddata = function() {
        video.currentTime = 1;
      };
      video.onseeked = function() {
        var canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        self.filePreview = canvas.toDataURL('image/jpeg');
        URL.revokeObjectURL(video.src);
      };
      video.src = URL.createObjectURL(file);
      e.target.value = '';
    },
    clearFile() { this.fileToSend = null; this.filePreview = null; },
    openImage(url) { this.lightboxUrl = this.pu(url); },
    // Lightbox controls
    zoomIn() { this.lbScale = Math.min(this.lbScale * 1.3, 8); },
    zoomOut() { this.lbScale = Math.max(this.lbScale / 1.3, 0.5); if (this.lbScale <= 1) { this.lbX = 0; this.lbY = 0; } },
    resetZoom() { this.lbScale = 1; this.lbX = 0; this.lbY = 0; },
    downloadImage() { if (this.lightboxUrl) { var a = document.createElement('a'); a.href = this.lightboxUrl; a.download = 'image'; a.target = '_blank'; a.click(); } },
    onWheel(e) { if (e.deltaY < 0) this.zoomIn(); else this.zoomOut(); },
    dragStart(e) { if (this.lbScale <= 1) return; this.lbDragging = true; this.lbDragStartX = e.clientX - this.lbX; this.lbDragStartY = e.clientY - this.lbY; },
    dragMove(e) { if (!this.lbDragging) return; this.lbX = e.clientX - this.lbDragStartX; this.lbY = e.clientY - this.lbDragStartY; },
    dragEnd() { this.lbDragging = false; },
    touchStart(e) {
      if (e.touches.length === 2) {
        var dx = e.touches[0].clientX - e.touches[1].clientX;
        var dy = e.touches[0].clientY - e.touches[1].clientY;
        this.lbTouchDist = Math.sqrt(dx * dx + dy * dy);
      } else if (e.touches.length === 1 && this.lbScale > 1) {
        this.lbDragging = true;
        this.lbDragStartX = e.touches[0].clientX - this.lbX;
        this.lbDragStartY = e.touches[0].clientY - this.lbY;
      }
    },
    touchMove(e) {
      if (e.touches.length === 2 && this.lbTouchDist > 0) {
        var dx = e.touches[0].clientX - e.touches[1].clientX;
        var dy = e.touches[0].clientY - e.touches[1].clientY;
        var dist = Math.sqrt(dx * dx + dy * dy);
        var ratio = dist / this.lbTouchDist;
        this.lbScale = Math.max(0.5, Math.min(8, this.lbScale * ratio));
        this.lbTouchDist = dist;
      } else if (e.touches.length === 1 && this.lbDragging) {
        this.lbX = e.touches[0].clientX - this.lbDragStartX;
        this.lbY = e.touches[0].clientY - this.lbDragStartY;
      }
    },
    touchEnd() { this.lbDragging = false; this.lbTouchDist = 0; },
    openDoc(url, name) {
      this.docViewUrl = this.pu(url);
      this.docViewName = name || this.getFileName(url);
    },
    getFileName(url) {
      if (!url) return 'Document';
      var parts = url.split('/');
      return parts[parts.length - 1] || 'Document';
    },
    downloadDoc() {
      if (!this.docViewUrl) return;
      var a = document.createElement('a'); a.href = this.docViewUrl; a.download = this.docViewName || 'document'; a.target = '_blank'; a.click();
    },
    openDocExternal() {
      if (this.docViewUrl) window.open(this.docViewUrl, '_blank');
    },
    openMap(msg) {
      var loc = this.parseLoc(msg);
      if (loc.lat && loc.lng) window.open('https://www.google.com/maps?q=' + loc.lat + ',' + loc.lng, '_blank');
    },
    parseLoc(msg) {
      var lat = msg.latitude || 0; var lng = msg.longitude || 0;
      if (msg.content) { try { var d = JSON.parse(msg.content); lat = d.latitude || lat; lng = d.longitude || lng; } catch(e) {} }
      return { lat: lat, lng: lng };
    },
    getLocName(msg) {
      if (msg.content) { try { var d = JSON.parse(msg.content); if (d.name) return d.name; } catch(e) {} }
      if (msg.location_name && msg.location_name !== 'My Location') return msg.location_name;
      return 'Shared Location';
    },
    getLocCoords(msg) {
      var loc = this.parseLoc(msg);
      if (!loc.lat || !loc.lng) return '';
      return Number(loc.lat).toFixed(5) + ', ' + Number(loc.lng).toFixed(5);
    },

    // PRESENCE
    async updateMyPresence() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) return;
      try {
        var f = new FormData();
        f.append('function', 'updatePresence');
        f.append('userId', userId);
        await execute(f, flocker);
      } catch (e) {}
    },
    async fetchOtherStatus() {
      if (!this.otherId || this.destroyed) return;
      try {
        var f = new FormData();
        f.append('function', 'getUserStatus');
        f.append('userId', this.otherId);
        var r = await execute(f, flocker);
        if (this.destroyed) return;
        if (r.data && r.data.success) {
          this.otherOnline = r.data.data.is_online === '1' || r.data.data.is_online === 1;
          this.otherLastSeen = r.data.data.last_seen_at;
        }
      } catch (e) {}
    },
    startPresence() {
      this.updateMyPresence();
      this.fetchOtherStatus();
      var self = this;
      this.presenceTimer = setInterval(function() {
        if (self.destroyed) return;
        self.updateMyPresence();
        self.fetchOtherStatus();
      }, 60000);
    },
    stopPresence() { if (this.presenceTimer) { clearInterval(this.presenceTimer); this.presenceTimer = null; } },
    setMeOffline() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) return;
      var f = new FormData();
      f.append('function', 'setOffline');
      f.append('userId', userId);
      navigator.sendBeacon ? navigator.sendBeacon(flocker, f) : execute(f, flocker);
    },

    // MESSAGE ACTIONS
    isDeleted(msg) {
      var userId = sessionStorage.getItem('userId');
      if (this.isMe(msg) && (msg.is_deleted_by_sender === '1' || msg.is_deleted_by_sender === 1)) return true;
      if (!this.isMe(msg) && (msg.is_deleted_by_receiver === '1' || msg.is_deleted_by_receiver === 1)) return true;
      if (msg._deleted_for_all) return true;
      return false;
    },
    canEdit(msg) {
      if (!msg) return false;
      // Allow editing any message that has text content (text, photo with caption, etc.)
      // Only exclude voice_note, location, listing_card
      var noEdit = ['voice_note', 'voice', 'location', 'listing_card'];
      if (noEdit.indexOf(msg.message_type) > -1) return false;
      if (!msg.content || msg.content.trim() === '') return false;
      // Allow edit within 48 hours (like WhatsApp)
      var diff = (Date.now() - new Date(msg.created_at).getTime()) / 3600000;
      return diff < 48;
    },
    showMsgMenu(e, msg, idx) {
      if (this.isDeleted(msg)) return;
      var x = e.clientX; var y = e.clientY;
      // Keep menu on screen
      if (x + 200 > window.innerWidth) x = window.innerWidth - 210;
      if (y + 200 > window.innerHeight) y = y - 180;
      this.msgMenu = { x: x, y: y, msg: msg, idx: idx, mine: this.isMe(msg) };
    },
    touchHold(msg, idx, e) {
      if (this.isDeleted(msg)) return;
      var self = this;
      this.holdTimer = setTimeout(function() {
        var touch = e.touches[0];
        var x = touch.clientX; var y = touch.clientY;
        if (x + 200 > window.innerWidth) x = window.innerWidth - 210;
        if (y + 200 > window.innerHeight) y = y - 180;
        self.msgMenu = { x: x, y: y, msg: msg, idx: idx, mine: self.isMe(msg) };
      }, 500);
    },
    cancelHold() { if (this.holdTimer) { clearTimeout(this.holdTimer); this.holdTimer = null; } },
    copyMsg() {
      if (!this.msgMenu) return;
      var text = this.msgMenu.msg.content || '';
      if (this.isListingCard(this.msgMenu.msg)) { text = this.getCardText(this.msgMenu.msg) || this.parseCard(this.msgMenu.msg).title || ''; }
      navigator.clipboard.writeText(text).catch(function() {});
      this.msgMenu = null;
    },
    editMsg() {
      if (!this.msgMenu) return;
      this.editingMsg = this.msgMenu.msg;
      this.editText = this.msgMenu.msg.content || '';
      this.msgMenu = null;
      var self = this;
      this.$nextTick(function() { if (self.$refs.editInput) self.$refs.editInput.focus(); });
    },
    cancelEdit() { this.editingMsg = null; this.editText = ''; },
    async saveEdit() {
      if (!this.editingMsg || !this.editText.trim()) return;
      var userId = sessionStorage.getItem('userId');
      try {
        var f = new FormData();
        f.append('function', 'editMessage');
        f.append('messageId', this.editingMsg.id);
        f.append('userId', userId);
        f.append('content', this.editText.trim());
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          // Update local message
          var msg = this.messages.find(function(m) { return m.id === this.editingMsg.id; }.bind(this));
          if (msg) { msg.content = this.editText.trim(); msg.is_edited = '1'; }
        }
      } catch (e) { console.error(e); }
      this.cancelEdit();
    },
    async deleteForEveryone() {
      if (!this.msgMenu) return;
      var msg = this.msgMenu.msg;
      this.msgMenu = null;
      var userId = sessionStorage.getItem('userId');
      try {
        var f = new FormData();
        f.append('function', 'deleteMessage');
        f.append('messageId', msg.id);
        f.append('userId', userId);
        f.append('deleteType', 'everyone');
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          var m = this.messages.find(function(mm) { return mm.id === msg.id; });
          if (m) { m._deleted_for_all = true; m.content = ''; m.file_url = null; }
        }
      } catch (e) { console.error(e); }
    },
    async deleteForMe() {
      if (!this.msgMenu) return;
      var msg = this.msgMenu.msg;
      var mine = this.msgMenu.mine;
      this.msgMenu = null;
      var userId = sessionStorage.getItem('userId');
      try {
        var f = new FormData();
        f.append('function', 'deleteMessage');
        f.append('messageId', msg.id);
        f.append('userId', userId);
        f.append('deleteType', 'me');
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          var m = this.messages.find(function(mm) { return mm.id === msg.id; });
          if (m) {
            if (mine) m.is_deleted_by_sender = '1';
            else m.is_deleted_by_receiver = '1';
          }
        }
      } catch (e) { console.error(e); }
    },

    // LOCATION
    shareLocation() {
      this.showAttach = false;
      this.showLocPicker = true;
      this.locQuery = '';
      this.locResults = [];
      this.$nextTick(function() { if (this.$refs.locSearchInput) this.$refs.locSearchInput.focus(); }.bind(this));
    },
    shareCurrentLocation() {
      if (!navigator.geolocation) { alert('Geolocation not supported'); return; }
      this.locGettingGps = true;
      var self = this;
      navigator.geolocation.getCurrentPosition(function(pos) {
        self.locGettingGps = false;
        self.showLocPicker = false;
        self.sendLocation(pos.coords.latitude, pos.coords.longitude, 'My Current Location');
      }, function() {
        self.locGettingGps = false;
        alert('Could not get your location. Please enable location access.');
      }, { enableHighAccuracy: true, timeout: 10000 });
    },
    debounceLocSearch() {
      clearTimeout(this.locSearchTimer);
      if (!this.locQuery.trim()) { this.locResults = []; return; }
      var self = this;
      this.locSearchTimer = setTimeout(function() { self.searchPlaces(); }, 400);
    },
    async searchPlaces() {
      var q = this.locQuery.trim();
      if (!q) return;
      this.locSearching = true;
      try {
        var url = 'https://nominatim.openstreetmap.org/search?format=json&q=' + encodeURIComponent(q) + '&limit=8&countrycodes=ke&addressdetails=1';
        var resp = await fetch(url, { headers: { 'Accept-Language': 'en' } });
        var data = await resp.json();
        this.locResults = data.map(function(r) {
          return {
            name: r.display_name.split(',')[0],
            address: r.display_name,
            lat: parseFloat(r.lat),
            lng: parseFloat(r.lon)
          };
        });
      } catch (e) { console.error(e); this.locResults = []; }
      this.locSearching = false;
    },
    sharePlace(place) {
      this.showLocPicker = false;
      this.sendLocation(place.lat, place.lng, place.name);
    },
    shareCustom(label) {
      if (!navigator.geolocation) { alert('Geolocation not supported'); return; }
      this.locGettingGps = true;
      var self = this;
      navigator.geolocation.getCurrentPosition(function(pos) {
        self.locGettingGps = false;
        self.showLocPicker = false;
        self.sendLocation(pos.coords.latitude, pos.coords.longitude, label);
      }, function() {
        self.locGettingGps = false;
        alert('Could not get your location.');
      }, { enableHighAccuracy: true, timeout: 10000 });
    },
    async sendLocation(lat, lng, locName) {
      var userId = sessionStorage.getItem('userId');
      var conv = await this.getConv();
      if (!conv) return;
      var receiverId = (conv.participant_one_id === userId) ? conv.participant_two_id : conv.participant_one_id;
      var locData = JSON.stringify({ latitude: lat, longitude: lng, name: locName || 'Shared Location', _type: 'location' });
      try {
        var f = new FormData();
        f.append('function', 'sendMessage');
        f.append('senderId', userId);
        f.append('receiverId', receiverId);
        f.append('content', locData);
        f.append('messageType', 'location');
        if (conv.context_type) f.append('contextType', conv.context_type);
        if (conv.context_id) f.append('contextId', conv.context_id);
        if (conv.listing_id) f.append('listingId', conv.listing_id);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          await this.pollNew();
          this.scrollToBottom();
        }
      } catch (e) { console.error(e); }
    },

    // VOICE RECORDING
    async startRecording() {
      this.showEmoji = false; this.showAttach = false;
      try {
        var stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.mediaRecorder = new MediaRecorder(stream);
        this.audioChunks = [];
        var self = this;
        this.mediaRecorder.ondataavailable = function(e) { self.audioChunks.push(e.data); };
        this.mediaRecorder.onstop = function() {
          stream.getTracks().forEach(function(t) { t.stop(); });
        };
        this.mediaRecorder.start();
        this.recording = true;
        this.recStart = Date.now();
        this.recInterval = setInterval(function() {
          var s = Math.floor((Date.now() - self.recStart) / 1000);
          self.recTime = Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
        }, 500);
      } catch (e) { alert('Microphone access denied. Please allow microphone access.'); }
    },
    cancelRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
      this.recording = false; this.audioChunks = [];
      clearInterval(this.recInterval);
    },
    stopRecording() {
      var self = this;
      if (!this.mediaRecorder) return;
      this.mediaRecorder.onstop = function() {
        self.mediaRecorder.stream.getTracks().forEach(function(t) { t.stop(); });
        var blob = new Blob(self.audioChunks, { type: 'audio/webm' });
        var dur = self.recTime;
        self.sendVoice(blob, dur);
      };
      this.mediaRecorder.stop();
      this.recording = false;
      clearInterval(this.recInterval);
    },
    async sendVoice(blob, dur) {
      var userId = sessionStorage.getItem('userId');
      var conv = await this.getConv();
      if (!conv) return;
      var receiverId = (conv.participant_one_id === userId) ? conv.participant_two_id : conv.participant_one_id;
      var file = new File([blob], 'voice_' + Date.now() + '.webm', { type: 'audio/webm' });
      try {
        var f = new FormData();
        f.append('function', 'sendMessage');
        f.append('senderId', userId);
        f.append('receiverId', receiverId);
        f.append('content', dur);
        f.append('messageType', 'voice_note');
        f.append('file', file);
        if (conv.context_type) f.append('contextType', conv.context_type);
        if (conv.context_id) f.append('contextId', conv.context_id);
        if (conv.listing_id) f.append('listingId', conv.listing_id);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          await this.pollNew();
          this.scrollToBottom();
        }
      } catch (e) { console.error(e); }
    },
    playVoice(msg) {
      if (this.playingId === msg.id) { this.stopAudio(); return; }
      this.stopAudio();
      var url = msg.file_url;
      if (url && !url.startsWith('blob:')) url = this.pu(url);
      this.currentAudio = new Audio(url);
      this.playingId = msg.id;
      var self = this;
      this.currentAudio.onended = function() { self.playingId = null; self.currentAudio = null; };
      this.currentAudio.play().catch(function() { self.playingId = null; });
    },
    stopAudio() {
      if (this.currentAudio) { this.currentAudio.pause(); this.currentAudio = null; }
      this.playingId = null;
    },

    // POLLING
    startPolling() {
      var self = this;
      this.pollTimer = setInterval(function() { if (!self.destroyed) self.pollNew(); }, 15000);
    },
    stopPolling() { if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; } },
    async pollNew() {
      if (this.destroyed) return;
      var userId = sessionStorage.getItem('userId');
      try {
        var f = new FormData();
        f.append('function', 'getMessages');
        f.append('conversationId', this.conversationId);
        f.append('userId', userId);
        f.append('page', 1);
        f.append('limit', 10);
        var r = await execute(f, flocker);
        if (this.destroyed) return;
        if (r.data && r.data.success) {
          var fresh = r.data.data.messages || [];
          var existing = {};
          this.messages.forEach(function(m) { existing[m.id] = true; });
          var added = false;
          for (var i = 0; i < fresh.length; i++) {
            if (!existing[fresh[i].id]) {
              var optimisticIdx = this.findOptimisticMatch(fresh[i]);
              if (optimisticIdx > -1) {
                this.messages.splice(optimisticIdx, 1, Object.assign({}, this.messages[optimisticIdx], fresh[i], { _pending: false, _optimistic: false }));
              } else {
                this.messages.push(fresh[i]);
                added = true;
              }
            }
          }
          // Also update edited/deleted messages
          for (var j = 0; j < fresh.length; j++) {
            var local = this.messages.find(function(m) { return m.id === fresh[j].id; });
            if (local) {
              if (fresh[j].content !== local.content) local.content = fresh[j].content;
              if (fresh[j].is_edited !== local.is_edited) local.is_edited = fresh[j].is_edited;
              if (fresh[j].is_deleted_by_sender !== local.is_deleted_by_sender) local.is_deleted_by_sender = fresh[j].is_deleted_by_sender;
              if (fresh[j].is_deleted_by_receiver !== local.is_deleted_by_receiver) local.is_deleted_by_receiver = fresh[j].is_deleted_by_receiver;
            }
          }
          if (added) this.scrollToBottom();
          this.saveToCache();
        }
      } catch (e) {}
    },

    // GET CONVERSATION DATA
    async getConv() {
      if (this.convData) return this.convData;
      var userId = sessionStorage.getItem('userId');
      var cf = new FormData();
      cf.append('function', 'getConversations');
      cf.append('userId', userId);
      var cr = await execute(cf, flocker);
      if (cr.data && cr.data.success) {
        var self = this;
        var conv = (cr.data.data.conversations || []).find(function(c) { return c.id === self.conversationId; });
        if (conv) { this.convData = conv; return conv; }
      }
      return null;
    },

    // LOAD MESSAGES
    // CACHE HELPERS
    cacheKey() { return 'chat_' + this.conversationId; },
    convCacheKey() { return 'chat_conv_' + this.conversationId; },
    saveToCache() {
      try {
        sessionStorage.setItem(this.cacheKey(), JSON.stringify(this.messages.slice(-100)));
        if (this.convData) sessionStorage.setItem(this.convCacheKey(), JSON.stringify({ name: this.otherName, avatar: this.otherAvatar, otherId: this.otherId, contextLabel: this.contextLabel }));
      } catch (e) {}
    },
    loadFromCache() {
      try {
        var cached = sessionStorage.getItem(this.cacheKey());
        if (cached) { this.messages = JSON.parse(cached); return true; }
      } catch (e) {}
      return false;
    },
    loadConvFromCache() {
      try {
        var cached = sessionStorage.getItem(this.convCacheKey());
        if (cached) {
          var d = JSON.parse(cached);
          this.otherName = d.name || '';
          this.otherAvatar = d.avatar || '';
          this.otherId = d.otherId || '';
          this.contextLabel = d.contextLabel || '';
          return true;
        }
      } catch (e) {}
      return false;
    },
    goBack(){
      if (this.cameFromNotifications) { this.$router.push('/notifications'); return; }
      // Use browser history if there's somewhere to go back to, else fall back to inbox
      if (window.history.length > 1) { this.$router.go(-1); }
      else { this.$router.push('/messages'); }
    },
    showSenderAvatar(i){
      // Show avatar on the LAST message of a run from the same sender (so the bubble looks anchored to them)
      var sorted = this.sortedMessages;
      var cur = sorted[i];
      var nxt = sorted[i+1];
      if (!cur) return false;
      if (this.isMe(cur)) return false;
      if (!nxt) return true;
      return this.isMe(nxt) || nxt.sender_id !== cur.sender_id;
    },

    async loadMessages() {
      var userId = sessionStorage.getItem('userId');
      if (!userId) { this.$router.push('/login'); return; }

      // 1. Load from cache instantly
      var hadCache = this.loadFromCache();
      this.loadConvFromCache();
      if (hadCache) {
        this.loading = false;
        this.scrollToBottom();
      }

      // 2. Fetch fresh from server in background
      try {
        var f = new FormData();
        f.append('function', 'getMessages');
        f.append('conversationId', this.conversationId);
        f.append('userId', userId);
        f.append('page', this.page);
        f.append('limit', 50);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          this.messages = r.data.data.messages || [];
          this.hasMore = this.messages.length >= 50;
          this.saveToCache();
        }
        var conv = await this.getConv();
        if (conv) {
          var userId = sessionStorage.getItem('userId');
          this.otherName = conv.other_name || '';
          this.otherAvatar = conv.other_avatar || '';
          this.otherId = (conv.participant_one_id === userId) ? conv.participant_two_id : conv.participant_one_id;
          if (conv.context_type === 'listing') this.contextLabel = 'Listing inquiry';
          else if (conv.context_type === 'order') this.contextLabel = 'Order discussion';
          this.saveToCache();
        }
      } catch (e) { console.error(e); }
      this.loading = false;
      this.scrollToBottom();
      this.startPresence();
    },
    async loadMore() {
      this.loadingMore = true;
      this.page++;
      var userId = sessionStorage.getItem('userId');
      try {
        var f = new FormData();
        f.append('function', 'getMessages');
        f.append('conversationId', this.conversationId);
        f.append('userId', userId);
        f.append('page', this.page);
        f.append('limit', 50);
        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          var older = r.data.data.messages || [];
          var existing = {};
          this.messages.forEach(function(m) { existing[m.id] = true; });
          for (var i = 0; i < older.length; i++) {
            if (!existing[older[i].id]) this.messages.push(older[i]);
          }
          this.hasMore = older.length >= 50;
        }
      } catch (e) { console.error(e); }
      this.loadingMore = false;
    },


    buildTempMessage(tempId, userId, receiverId, text, pendingFile, pendingType, pendingPreview) {
      var nowIso = new Date().toISOString();
      var msgType = pendingFile ? pendingType : 'text';
      var temp = {
        id: tempId,
        sender_id: userId,
        receiver_id: receiverId,
        content: text,
        message_type: msgType,
        created_at: nowIso,
        updated_at: nowIso,
        _pending: true,
        _optimistic: true
      };
      if (msgType === 'photo' || msgType === 'image') {
        temp.file_url = pendingPreview || '';
      } else if (msgType === 'video') {
        temp.file_url = pendingPreview || '';
      } else if (msgType === 'file' || msgType === 'document') {
        temp.file_name = pendingFile && pendingFile.name ? pendingFile.name : 'Document';
        temp.file_size = pendingFile && pendingFile.size ? this.formatSize(pendingFile.size) : '';
      }
      return temp;
    },
    findOptimisticMatch(serverMsg) {
      var serverContent = (serverMsg && serverMsg.content ? String(serverMsg.content).trim() : '');
      var serverType = serverMsg && serverMsg.message_type ? serverMsg.message_type : 'text';
      var serverTime = serverMsg && serverMsg.created_at ? new Date(serverMsg.created_at).getTime() : 0;
      for (var i = 0; i < this.messages.length; i++) {
        var local = this.messages[i];
        var localId = String(local.id || '');
        if (!(local._optimistic || local._pending || localId.indexOf('temp-') === 0)) continue;
        if (String(local.sender_id || '') !== String(serverMsg.sender_id || '')) continue;
        if (String(local.message_type || 'text') !== String(serverType || 'text')) continue;
        var localContent = (local.content ? String(local.content).trim() : '');
        if (localContent !== serverContent) continue;
        var localTime = local.created_at ? new Date(local.created_at).getTime() : 0;
        if (serverTime && localTime && Math.abs(serverTime - localTime) > 5 * 60 * 1000) continue;
        return i;
      }
      return -1;
    },

    // SEND MESSAGE
    async send() {
      var text = this.newMsg.trim();
      if (!text && !this.fileToSend) return;
      if (this.sending) return;

      this.sending = true;
      this.showEmoji = false;
      this.showAttach = false;

      var pendingText = text;
      var pendingFile = this.fileToSend;
      var pendingPreview = this.filePreview;
      var pendingType = this.fileType;
      var userId = sessionStorage.getItem('userId');
      var tempId = 'temp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
      var tempMsg = null;

      this.newMsg = '';
      this.clearFile();
      if (this.$refs.msgInput) this.$refs.msgInput.style.height = 'auto';

      try {
        var conv = await this.getConv();
        if (!conv) {
          this.newMsg = pendingText;
          if (pendingFile) {
            this.fileToSend = pendingFile;
            this.filePreview = pendingPreview;
            this.fileType = pendingType;
          }
          this.sending = false;
          return;
        }

        var receiverId = (conv.participant_one_id === userId) ? conv.participant_two_id : conv.participant_one_id;

        tempMsg = this.buildTempMessage(tempId, userId, receiverId, pendingText, pendingFile, pendingType, pendingPreview);
        this.messages.push(tempMsg);
        this.saveToCache();
        this.scrollToBottom();

        var f = new FormData();
        f.append('function', 'sendMessage');
        f.append('senderId', userId);
        f.append('receiverId', receiverId);
        f.append('content', pendingText);
        if (conv.context_type) f.append('contextType', conv.context_type);
        if (conv.context_id) f.append('contextId', conv.context_id);
        if (conv.listing_id) f.append('listingId', conv.listing_id);

        var msgType = 'text';
        if (pendingFile) {
          msgType = pendingType;
          f.append('messageType', msgType);
          f.append('file', pendingFile);
        }

        var r = await execute(f, flocker);
        if (r.data && r.data.success) {
          var tempIndex = this.messages.findIndex(function(m) { return m.id === tempId; });
          if (tempIndex > -1) {
            if (r.data.data && r.data.data.message) {
              this.messages.splice(tempIndex, 1, Object.assign({}, r.data.data.message, { _pending: false, _optimistic: false }));
            } else {
              this.messages.splice(tempIndex, 1, Object.assign({}, this.messages[tempIndex], { _pending: false }));
            }
          }
          this.saveToCache();
          this.scrollToBottom();
          this.pollNew();
        } else {
          throw new Error((r.data && r.data.message) || 'Failed to send message');
        }
      } catch (e) {
        console.error(e);
        var rollbackIndex = this.messages.findIndex(function(m) { return m.id === tempId; });
        if (rollbackIndex > -1) this.messages.splice(rollbackIndex, 1);

        this.newMsg = pendingText;
        if (pendingFile) {
          this.fileToSend = pendingFile;
          this.filePreview = pendingPreview;
          this.fileType = pendingType;
        }
        this.$nextTick(function() {
          if (this.$refs.msgInput) {
            this.$refs.msgInput.focus();
            this.autoGrow();
          }
        }.bind(this));
      }
      this.sending = false;
    }
  }
};
</script>
